import { Routes, Route, Navigate } from 'react-router-dom';
import { Box, CircularProgress, Typography, Button } from '@mui/material';
import WifiOffIcon from '@mui/icons-material/WifiOff';
import { useUser } from './lib/auth-client';
import { useGlobalErrorHandler } from './lib/errorHandler';
import { useEffect } from 'react';
import { useAppDispatch, useAppSelector } from './store/hooks';
import { fetchAllFilters } from './store/slices/filtersSlice';

import Layout from './components/Layout/Layout.tsx';
import LoginPage from './pages/Auth/LoginPage.tsx';
import ForgotPasswordPage from './pages/Auth/ForgotPasswordPage.tsx';
import ResetPasswordPage from './pages/Auth/ResetPasswordPage.tsx';
import { DashboardPage } from './pages/Dashboard';
import { AdmissionMethodsPage } from './pages/AdmissionMethods';
import FacultiesPage from './pages/Faculties/FacultiesPage.tsx';
import MajorsPage from './pages/Majors/MajorsPage.tsx';
import { ProvincesOriginalPage } from './pages/ProvincesOriginal';
import { ProvincesMergedPage } from './pages/ProvincesMerged';
import THPTPage from './pages/THPT/THPTPage.tsx';
import Top149Page from './pages/Top149/Top149Page.tsx';
import ProfilePage from './pages/Profile/ProfilePage.tsx';
import AccountManagementPage from './pages/Users/AccountManagementPage.tsx';
import { AdminPage, DataImportPage, DataExportPage } from './pages/Admin';
import { ProvinceConversionsPage } from './pages/Admin/ProvinceConversions';
import NotFoundPage from './pages/NotFoundPage.tsx';
import ErrorPage from './pages/ErrorPage.tsx';

function App() {
	// Enable global error handling
	useGlobalErrorHandler();

	// Redux dispatch and selectors
	const dispatch = useAppDispatch();
	const filtersInitialized = useAppSelector(state => state.filters.initialized);

	// Use useUser hook to sync auth state with Redux
	const { user, isLoading: isCheckingAuth, isReconnecting, retry } = useUser();

	// Fetch all filters once when app loads and user is authenticated
	useEffect(() => {
		if (user && !filtersInitialized) {
			dispatch(fetchAllFilters());
		}
	}, [user, filtersInitialized, dispatch]);

	// Show loading spinner while checking authentication
	if (isCheckingAuth) {
		return (
			<Box
				display="flex"
				justifyContent="center"
				alignItems="center"
				minHeight="100vh"
			>
				<CircularProgress />
			</Box>
		);
	}

	// Backend unreachable and we don't have a known user yet/anymore: this is
	// NOT a logout, it's a connectivity outage. Show a distinct "reconnecting"
	// state instead of bouncing to the login page (auto-recovers via useUser).
	if (!user && isReconnecting) {
		return (
			<Box
				display="flex"
				flexDirection="column"
				alignItems="center"
				justifyContent="center"
				gap={2}
				minHeight="100vh"
				textAlign="center"
				px={2}
			>
				<WifiOffIcon color="disabled" sx={{ fontSize: 48 }} />
				<Typography variant="h6">Mất kết nối máy chủ</Typography>
				<Typography variant="body2" color="text.secondary">
					Đang thử kết nối lại tự động. Vui lòng chờ trong giây lát...
				</Typography>
				<CircularProgress size={24} />
				<Button variant="outlined" onClick={retry}>
					Thử lại ngay
				</Button>
			</Box>
		);
	}

	// If not authenticated, show login page
	if (!user) {
		return (
			<Routes>
				<Route path="/login" element={<LoginPage />} />
				<Route path="/forgot-password" element={<ForgotPasswordPage />} />
				<Route path="/reset-password" element={<ResetPasswordPage />} />
				<Route path="/error" element={<ErrorPage />} />
				<Route path="*" element={<Navigate to="/login" replace />} />
			</Routes>
		);
	}

	// If authenticated, show main app
	return (
		<Routes>
			<Route path="/login" element={<Navigate to="/dashboard" replace />} />
			{/* Reachable while signed in too: the reset link in the email may be opened in a browser with a live session. */}
			<Route path="/reset-password" element={<ResetPasswordPage />} />
			<Route path="/error" element={<ErrorPage />} />
			<Route path="/" element={<Layout />}>
				<Route index element={<Navigate to="/dashboard" replace />} />
				<Route path="dashboard" element={<DashboardPage />} />
				<Route path="faculties" element={<FacultiesPage />} />
				<Route path="admission-methods" element={<AdmissionMethodsPage />} />
				<Route path="majors" element={<MajorsPage />} />
				<Route path="provinces-original" element={<ProvincesOriginalPage />} />
				<Route path="provinces-merged" element={<ProvincesMergedPage />} />
				<Route path="thpt" element={<THPTPage />} />
				<Route path="top149" element={<Top149Page />} />
				<Route path="profile" element={<ProfilePage />} />
				<Route path="settings" element={<ProfilePage />} />
				<Route path="system" element={<AdminPage />} />
				<Route path="system/account-management" element={<AccountManagementPage />} />
				<Route path="system/nhap-du-lieu" element={<DataImportPage />} />
				<Route path="system/xuat-du-lieu" element={<DataExportPage />} />
				<Route path="system/chuyen-doi-tinh" element={<ProvinceConversionsPage />} />
				<Route path="admin" element={<Navigate to="/system" replace />} />
				<Route path="admin/account-management" element={<Navigate to="/system/account-management" replace />} />
				<Route path="admin/nhap-du-lieu" element={<Navigate to="/system/nhap-du-lieu" replace />} />
				<Route path="admin/xuat-du-lieu" element={<Navigate to="/system/xuat-du-lieu" replace />} />
				<Route path="*" element={<NotFoundPage />} />
			</Route>
		</Routes>
	);
}

export default App;
